import React, { createContext, useContext, useState, useRef, useEffect } from "react";

const SelectContext = createContext();

export const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error("Select components must be used within <Select>");
  }
  return context;
};

const Select = ({ value, onChange, error = false, searchable = false, placeholder = "Select an option...", size = "medium", fullwidth = false, children }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [minWidth, setMinWidth] = useState(0);
  const triggerRef = useRef(null);
  const measureRef = useRef(null);

  const toggleOpen = () => setOpen(!open);
  const close = () => {
    setOpen(false);
    setSearchTerm("");
  };
  const selectValue = (val) => {
    onChange(val);
    close();
  };

  const clearSearch = () => setSearchTerm("");

  // Get size-specific measurements
  const getSizeMeasurements = () => {
    switch (size) {
      case "small":
        return { height: 32, padding: "6px 10px", iconGap: 24 };
      case "large":
        return { height: 48, padding: "12px 16px", iconGap: 40 };
      default: // medium
        return { height: 40, padding: "8px 12px", iconGap: 32 };
    }
  };

  const sizeMeasurements = getSizeMeasurements();

  // Calculate minimum width needed based on content
  useEffect(() => {
    if (!measureRef.current) return;

    // Create a temporary measuring element
    const measurer = document.createElement("div");
    measurer.style.position = "absolute";
    measurer.style.visibility = "hidden";
    measurer.style.whiteSpace = "nowrap";
    measurer.style.padding = sizeMeasurements.padding; // Use size-specific padding
    measurer.style.fontSize = window.getComputedStyle(document.body).fontSize;
    measurer.style.fontFamily = window.getComputedStyle(document.body).fontFamily;

    document.body.appendChild(measurer);

    let maxWidth = 0;

    // Measure placeholder/current value
    const currentText = typeof children === "string" ? children : placeholder;
    measurer.textContent = currentText;
    maxWidth = Math.max(maxWidth, measurer.offsetWidth);

    // Find all SelectItem children and measure their content
    const measureItems = (element) => {
      React.Children.forEach(element, (child) => {
        if (child && child.type && child.type.name === "SelectItem") {
          const text = typeof child.props.children === "string" ? child.props.children : "";
          if (text) {
            measurer.textContent = text;
            maxWidth = Math.max(maxWidth, measurer.offsetWidth);
          }
        } else if (child && child.props && child.props.children) {
          measureItems(child.props.children);
        }
      });
    };

    measureItems(children);

    // Add some extra space for the chevron icon and gap (size-dependent)
    maxWidth += sizeMeasurements.iconGap;

    document.body.removeChild(measurer);
    setMinWidth(maxWidth);
  }, [children, placeholder, size, sizeMeasurements.padding, sizeMeasurements.iconGap]);

  return (
    <SelectContext.Provider
      value={{
        open,
        toggleOpen,
        close,
        value,
        selectValue,
        triggerRef,
        error,
        searchable,
        searchTerm,
        setSearchTerm,
        clearSearch,
        placeholder,
        minWidth,
        size,
        sizeMeasurements,
        fullwidth,
      }}
    >
      <div ref={measureRef} style={{ position: "relative", display: "inline-block", width: fullwidth ? "100%" : "auto", minWidth: fullwidth ? "100%" : minWidth }}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

export default Select;
